import i18next from "src/i18n/config";
import { type WorkspaceSettingChange, workspaceSettingChangeSchema } from "src/services/eventVersionService/schemas/workspace";

import { WorkspaceEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";
import {
    findWorkspaceSettingLabelKey,
    isWorkspaceOptionSetting,
    WorkspaceOptionSettingName,
} from "@workspaces/components/WorkspaceConfigurationPanel/WorkspaceConfigurationPanel.utils";

import type { ChangedField } from "./shared/changedFields";
import { ChangedFieldsList } from "./shared/ChangedFieldsList";
import { getActorTitle } from "./shared/eventHelpers";
import type { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type WorkspaceConfigurationChangedEvent = Extract<WorkspaceEvents, { eventTypeName: "WorkspaceEvent.ConfigurationChanged" }>;

const isSettingChange = (value: unknown): value is WorkspaceSettingChange =>
    workspaceSettingChangeSchema.safeParse(value).success;

// Each of these settings reads as an instruction where it is switched ("Show the recent activity chart"),
// which cannot be quoted inside a sentence about what someone did. They get a sentence of their own instead,
// written out per setting so a translator sees the whole thing and can reorder it freely.
//
// Every key is spelled out inside its own t() call: key extraction runs with removeUnusedKeys, and it only
// recognizes a literal passed to t(), so a key reached through a lookup table is deleted from the locale file.
const describeOptionSetting = (name: WorkspaceOptionSettingName, isEnabled: boolean, actorTitle: string | undefined) => {
    switch (name) {
        case "DownloadsPageShowsPublicProducts":
            return isEnabled
                ? i18next.t("modals:eventModals.workspaceConfigurationPublicProductsShownDescription", { actorTitle })
                : i18next.t("modals:eventModals.workspaceConfigurationPublicProductsHiddenDescription", { actorTitle });
        case "MembersRequireTwoFactorAuthentication":
            return isEnabled
                ? i18next.t("modals:eventModals.workspaceConfigurationTwoFactorRequiredDescription", { actorTitle })
                : i18next.t("modals:eventModals.workspaceConfigurationTwoFactorNotRequiredDescription", { actorTitle });
        case "OverviewPageShowsActivityChart":
            return isEnabled
                ? i18next.t("modals:eventModals.workspaceConfigurationActivityChartShownDescription", { actorTitle })
                : i18next.t("modals:eventModals.workspaceConfigurationActivityChartHiddenDescription", { actorTitle });
    }
};

const describeSetting = (name: string, change: WorkspaceSettingChange, actorTitle: string | undefined): string | null => {
    if (change.new !== 0 && change.new !== 1) {
        return null;
    }

    const isEnabled = change.new === 1;

    if (isWorkspaceOptionSetting(name)) {
        return describeOptionSetting(name, isEnabled, actorTitle);
    }

    // A page toggle is labelled with the page's own name, so "enabled Groups" would not say what was
    // enabled. Its sentence names the page instead.
    const labelKey = findWorkspaceSettingLabelKey(name);
    if (!labelKey) {
        return null;
    }

    const descriptionKey = isEnabled
        ? "modals:eventModals.workspaceConfigurationPageEnabledDescription"
        : "modals:eventModals.workspaceConfigurationPageDisabledDescription";

    return i18next.t(descriptionKey, { actorTitle, setting: i18next.t(labelKey) });
};

const toChangedFields = (changes: [string, WorkspaceSettingChange][]): ChangedField[] =>
    changes.map(([name, change]) => ({
        path: name,
        removed: change.old === null ? [] : [change.old],
        added: [change.new],
    }));

const renderWorkspaceConfigurationChanged = (
    props: RendererProps<WorkspaceConfigurationChangedEvent>,
): EventContentResult | null => {
    const { event, theme } = props;
    const actorTitle = getActorTitle(event, i18next.t("common:status.unknown"));
    const header = i18next.t("modals:eventModals.workspaceConfigurationChangedTitle");
    const color = getEventColors(theme)[event.level];

    const changes = Object.entries(event.data ?? {}).filter((entry): entry is [string, WorkspaceSettingChange] =>
        isSettingChange(entry[1]),
    );

    if (!changes.length) {
        return {
            color,
            header,
            component: i18next.t("modals:eventModals.workspaceConfigurationChangedDescriptionWithoutData", { actorTitle }),
        };
    }

    if (changes.length === 1) {
        const [name, change] = changes[0];
        const description = describeSetting(name, change, actorTitle);

        if (description) {
            return { color, header, component: description };
        }
    }

    return {
        color,
        header,
        component: i18next.t("modals:eventModals.workspaceConfigurationChangedDescription", {
            actorTitle,
            count: changes.length,
        }),
        details: <ChangedFieldsList fields={toChangedFields(changes)} />,
    };
};

export const renderWorkspaceEventContent = (
    event: WorkspaceEvents,
    context: EventContext,
    theme: Theme,
): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "WorkspaceEvent.ConfigurationChanged":
            return renderWorkspaceConfigurationChanged({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
